"use client";

import { c as _c } from "react/compiler-runtime";
import { QueryKeys } from "@/constants/queryKeys";
import { infiniteQueryOptions, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import useSession from "@/hooks/QueryHooks/useSession";
import { getConversationByIdAction } from "../actions";
import { useEffect } from "react";
import { useSocketContext } from "@/components/providers/SocketProvider/SocketProvider";
import { WEBSOCKET_EVENTS } from "@/constants/websocket";
export const USER_CONVERSATION_BYID__QUERY_KEYS = {
  all: [QueryKeys.CHAT_CONVERSATIONS],
  getConversationById: (conversationId: string) => [...USER_CONVERSATION_BYID__QUERY_KEYS.all, "conversation-by-id", conversationId]
};
export function getConversationByIdQueryOptions({
  userId,
  conversationId
}: {
  userId?: string;
  conversationId?: string;
} = {}) {
  return infiniteQueryOptions({
    queryKey: USER_CONVERSATION_BYID__QUERY_KEYS.getConversationById(conversationId),
    queryFn: ({
      pageParam = 1
    }) => getConversationByIdAction({
      userId,
      conversationId,
      pageParam
    }),
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      const {
        metadata
      } = lastPage;
      const first = metadata?.pagination?.first || 0;
      const totalItems = Number(metadata?.pagination?.total || 0);
      if (!metadata?.pagination?.perPage) return;
      const currentPage = Math.floor(first / metadata?.pagination?.perPage) + 1;
      const totalPages = Math.ceil(totalItems / metadata?.pagination?.perPage);
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    enabled: !!userId && !!conversationId
  });
}
export const useConversationById = conversationId => {
  const $ = _c(13);
  const {
    data: session
  } = useSession();
  const {
    socket
  } = useSocketContext();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== conversationId || $[1] !== queryClient || $[2] !== session?.id || $[3] !== socket) {
    t0 = () => {
      if (!socket || !session?.id) {
        return;
      }
      const handleUserOnline = t1 => {
        const {
          userId
        } = t1;
        queryClient.setQueryData(USER_CONVERSATION_BYID__QUERY_KEYS.getConversationById(conversationId), oldData => {
          if (!oldData) {
            return oldData;
          }
          const firstPage = oldData.pages?.[0];
          if (!firstPage) {
            return oldData;
          }
          if (firstPage.data.conversation.otherUser.id !== userId) {
            return oldData;
          }
          return {
            ...oldData,
            pages: oldData.pages.map(_temp)
          };
        });
      };
      const handleUserOffline = t2 => {
        const {
          userId: userId_0
        } = t2;
        queryClient.setQueryData(USER_CONVERSATION_BYID__QUERY_KEYS.getConversationById(conversationId), oldData_0 => {
          if (!oldData_0) {
            return oldData_0;
          }
          const firstPage_0 = oldData_0.pages?.[0];
          if (!firstPage_0) {
            return oldData_0;
          }
          if (firstPage_0.data.conversation.otherUser.id !== userId_0) {
            return oldData_0;
          }
          return {
            ...oldData_0,
            pages: oldData_0.pages.map(_temp2)
          };
        });
      };
      socket.on(WEBSOCKET_EVENTS.USER_ONLINE, handleUserOnline);
      socket.on(WEBSOCKET_EVENTS.USER_OFFLINE, handleUserOffline);
      return () => {
        socket.off(WEBSOCKET_EVENTS.USER_ONLINE, handleUserOnline);
        socket.off(WEBSOCKET_EVENTS.USER_OFFLINE, handleUserOffline);
      };
    };
    $[0] = conversationId;
    $[1] = queryClient;
    $[2] = session?.id;
    $[3] = socket;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const t1 = session?.id;
  let t2;
  if ($[5] !== conversationId || $[6] !== queryClient || $[7] !== socket || $[8] !== t1) {
    t2 = [socket, t1, queryClient, conversationId];
    $[5] = conversationId;
    $[6] = queryClient;
    $[7] = socket;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  useEffect(t0, t2);
  const t3 = session?.id;
  let t4;
  if ($[10] !== conversationId || $[11] !== t3) {
    t4 = getConversationByIdQueryOptions({
      userId: t3,
      conversationId
    });
    $[10] = conversationId;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return useInfiniteQuery(t4);
};
function _temp(page, index) {
  if (index === 0) {
    return {
      ...page,
      data: {
        ...page.data,
        conversation: {
          ...page.data.conversation,
          otherUser: {
            ...page.data.conversation.otherUser,
            isOnline: true
          }
        }
      }
    };
  }
  return page;
}
function _temp2(page_0, index_0) {
  if (index_0 === 0) {
    return {
      ...page_0,
      data: {
        ...page_0.data,
        conversation: {
          ...page_0.data.conversation,
          otherUser: {
            ...page_0.data.conversation.otherUser,
            isOnline: false
          }
        }
      }
    };
  }
  return page_0;
}